import type { Signature } from 'entities/document/api';
import type { Workflow } from 'entities/workflow/model';
import { signatures as signaturesCatalog } from 'shared/signatures/catalog';

/**
 * Compute the signatures for a document by merging the document's own
 * signatures with the approved participants found in its workflow.
 *
 * Reads the canonical entities/workflow `Workflow` shape: `node.parameters`,
 * participant `type: 'participant-user' | 'participant-group'`, `p.parameters`
 * (a group's members live under `p.parameters.participants`). The per-participant
 * `parameters` payload is the same queue participant the legacy shape exposed as
 * `p.data`, so the produced Signature[] is unchanged.
 *
 * @param signatures - The list of signatures related to the document.
 * @param workflow - The workflow related to the document, if available.
 * @returns The computed signatures from signature list and workflow data.
 */
export const computedSignatures = (
  signatures: Signature[],
  workflow?: Workflow,
) => {
  if (!workflow) {
    return signatures;
  }

  const signeNodes = workflow.nodes.filter(node => node.type === 'signe');
  const participant: any[] = [];

  signeNodes.forEach(node => {
    (node as any).parameters.participants.forEach((p: any) => {
      if (p.type === 'participant-user') {
        participant.push(p.parameters);
      } else {
        p.parameters.participants.forEach((member: any) => {
          participant.push(member.parameters);
        });
      }
    });
  });

  const workflowSignatures: Signature[] = participant
    .filter((p: any) => p.approvedAt)
    .map((p: any) => ({
      signer: `${p.user.name} ${p.user.surname}`,
      issuer: '',
      country: '',
      signedAt: p.approvedAt,
      isValid: true,
      qualification: signaturesCatalog.getSignatureByCode(p.signMethod)!
        .qualification,
      format: '',
    }));

  return [...signatures, ...workflowSignatures];
};
